import { useState } from 'react';

import { useChat } from '../../hooks/useChat';
import BroadcastPanel from './tools/BroadcastPanel';
import AutoReplyPanel from './tools/AutoReplyPanel';
import QuickRepliesPanel from './tools/QuickRepliesPanel';
import SchedulePanel from './tools/SchedulePanel';
import RemindersPanel from './tools/RemindersPanel';
import DataExtractorPanel from './tools/DataExtractorPanel';
import ToolsPanel from './tools/ToolsPanel';

type ToolKey =
  | 'broadcast'
  | 'autoreply'
  | 'schedule'
  | 'reminders'
  | 'quick-replies'
  | 'data-extractor'
  | 'tools';

type Tool = {
  key: ToolKey;
  label: string;
  icon: string;
  description: string;
};

const TOOLS: Tool[] = [
  { key: 'broadcast', label: 'Broadcast', icon: '📤', description: 'Bulk berichten versturen' },
  { key: 'autoreply', label: 'Autoreply BOT', icon: '🤖', description: 'Automatische AI replies' },
  { key: 'schedule', label: 'Schedule', icon: '⏰', description: 'Geplande berichten' },
  { key: 'reminders', label: 'Reminders', icon: '🔔', description: 'Herinneringen' },
  { key: 'quick-replies', label: 'Quick Replies', icon: '💬', description: 'Snelle antwoorden' },
  { key: 'data-extractor', label: 'Data Extractor', icon: '📋', description: 'Gegevens extraheren' },
  { key: 'tools', label: 'Tools', icon: '🛠️', description: 'Handige tools' },
];

const ToolsSidebar = () => {
  const { chats, settings, setGlobalAutomode, activeChat, systemStatus, messages } = useChat();
  const [activeTool, setActiveTool] = useState<ToolKey | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (collapsed) {
    return (
      <aside className="tools-sidebar collapsed" aria-label="Tools">
        <button
          type="button"
          className="tools-expand"
          onClick={() => setCollapsed(false)}
          aria-label="Tools uitklappen"
        >
          ◀
        </button>
      </aside>
    );
  }

  const activeToolDef = TOOLS.find((t) => t.key === activeTool);

  return (
    <>
      <button
        type="button"
        className="mobile-tools-toggle"
        onClick={() => setMobileOpen(true)}
        aria-label="Tools openen"
      >
        🛠️
      </button>
      {mobileOpen && (
        <div
          className="tools-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`tools-sidebar ${mobileOpen ? 'mobile-open' : ''}`}
        aria-label="Tools"
      >
      <header className="tools-header">
        <h2>Tools</h2>
        <button
          type="button"
          className="button icon"
          onClick={() => setCollapsed(true)}
          aria-label="Tools inklappen"
        >
          ▶
        </button>
      </header>
      <nav className="tools-nav">
        {TOOLS.map((tool) => (
          <button
            key={tool.key}
            type="button"
            className={`tool-item ${activeTool === tool.key ? 'active' : ''}`}
            onClick={() => setActiveTool(activeTool === tool.key ? null : tool.key)}
            title={tool.description}
          >
            <span className="tool-icon" aria-hidden="true">{tool.icon}</span>
            <span className="tool-label">{tool.label}</span>
            {tool.key === 'autoreply' && settings?.globalAutomode && (
              <span className="tool-status-badge running">Aan</span>
            )}
          </button>
        ))}
      </nav>
      {activeTool && activeToolDef && (
        <div className="tool-panel-container">
          <header className="tool-panel-header">
            <span className="tool-panel-icon">{activeToolDef.icon}</span>
            <h3>{activeToolDef.label}</h3>
            <button
              type="button"
              className="button icon small"
              onClick={() => setActiveTool(null)}
              aria-label="Sluiten"
            >
              ×
            </button>
          </header>
          <div className="tool-panel-body">
            {activeTool === 'broadcast' && <BroadcastPanel chats={chats} />}
            {activeTool === 'autoreply' && (
              <AutoReplyPanel
                enabled={settings?.globalAutomode ?? false}
                onToggle={setGlobalAutomode}
                activeChat={activeChat}
                systemStatus={systemStatus}
              />
            )}
            {activeTool === 'schedule' && <SchedulePanel chats={chats} />}
            {activeTool === 'reminders' && <RemindersPanel chats={chats} />}
            {activeTool === 'quick-replies' && <QuickRepliesPanel activeChat={activeChat} />}
            {activeTool === 'data-extractor' && (
              <DataExtractorPanel activeChat={activeChat} messages={messages} />
            )}
            {activeTool === 'tools' && <ToolsPanel />}
          </div>
        </div>
      )}
    </aside>
    </>
  );
};

export default ToolsSidebar;
